import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Stack,
  Grid,
  Card,
  CardContent,
  Tooltip,
  Menu,
  ListItemIcon,
  ListItemText,
  MenuItem as MenuItemAction,
  Divider,
  Badge
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Build as MaintenanceIcon,
  Assignment as CertificationIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  MonetizationOn as MoneyIcon
} from '@mui/icons-material';
import { getActiveVesselsByPropulsionType, updateVesselStatusEnhanced, deleteVessel } from '../../../../firebase/services/vesselService';
import { Vessel } from '../../../../models/types';

const PoweredVesselsPage: React.FC = () => {
  const navigate = useNavigate();
  const [vessels, setVessels] = useState<Vessel[]>([]);
  const [filteredVessels, setFilteredVessels] = useState<Vessel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Table pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [manufacturerFilter, setManufacturerFilter] = useState('all');
  
  // Dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedVessel, setSelectedVessel] = useState<Vessel | null>(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  
  // Stats
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    maintenance: 0,
    outOfService: 0,
    needingAttention: 0
  });

  useEffect(() => {
    loadVessels();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [vessels, searchTerm, statusFilter, typeFilter, manufacturerFilter]);

  const loadVessels = async () => {
    try {
      setLoading(true);
      const poweredVessels = await getActiveVesselsByPropulsionType('powered');
      setVessels(poweredVessels);
      calculateStats(poweredVessels);
    } catch (err) {
      console.error('Error loading powered vessels:', err);
      setError('Failed to load powered vessels');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (vesselList: Vessel[]) => {
    const total = vesselList.length;
    const active = vesselList.filter(v => v.status === 'active').length;
    const maintenance = vesselList.filter(v => v.status === 'maintenance').length;
    const outOfService = vesselList.filter(v => v.status === 'out-of-service').length;
    
    // Calculate vessels needing attention (maintenance due, certifications expiring, etc.)
    const now = new Date();
    const oneMonthFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    const needingAttention = vesselList.filter(vessel => {
      // Check if maintenance is due
      if (vessel.nextMaintenanceDue && new Date(vessel.nextMaintenanceDue) <= oneMonthFromNow) {
        return true;
      }
      // Check if certifications are expiring
      if (vessel.certifications) {
        return vessel.certifications.some(cert => 
          new Date(cert.expiryDate) <= oneMonthFromNow
        );
      }
      return false;
    }).length;

    setStats({ total, active, maintenance, outOfService, needingAttention });
  };

  const applyFilters = () => {
    let filtered = vessels;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(vessel =>
        vessel.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vessel.manufacturer.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vessel.model.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vessel.stateRegistrationNumber.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(vessel => vessel.status === statusFilter);
    }

    // Type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(vessel => vessel.type === typeFilter);
    }

    // Manufacturer filter
    if (manufacturerFilter !== 'all') {
      filtered = filtered.filter(vessel => vessel.manufacturer === manufacturerFilter);
    }

    setFilteredVessels(filtered);
  };

  const handleDeleteVessel = async () => {
    if (!selectedVessel) return;
    
    try {
      await updateVesselStatusEnhanced(selectedVessel.id, 'deleted', 'Deleted by admin');
      setSuccess('Vessel deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedVessel(null);
      loadVessels();
    } catch (err) {
      console.error('Error deleting vessel:', err);
      setError('Failed to delete vessel');
    }
  };

  const handleStatusChange = async (vesselId: string, newStatus: 'active' | 'inactive' | 'maintenance' | 'out-of-service') => {
    try {
      await updateVesselStatusEnhanced(vesselId, newStatus, `Status changed to ${newStatus}`);
      setSuccess(`Vessel status updated to ${newStatus}`);
      loadVessels();
    } catch (err) {
      console.error('Error updating vessel status:', err);
      setError('Failed to update vessel status');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'default';
      case 'maintenance': return 'warning';
      case 'out-of-service': return 'error';
      default: return 'default';
    }
  };

  const getVesselAlerts = (vessel: Vessel) => {
    const alerts = [];
    const now = new Date();
    const oneMonthFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

    // Check maintenance due
    if (vessel.nextMaintenanceDue && new Date(vessel.nextMaintenanceDue) <= oneMonthFromNow) {
      alerts.push('maintenance');
    }

    // Check certification expiry
    if (vessel.certifications) {
      const expiringSoon = vessel.certifications.some(cert => 
        new Date(cert.expiryDate) <= oneMonthFromNow
      );
      if (expiringSoon) {
        alerts.push('certification');
      }
    }

    return alerts;
  };

  const handleActionMenuClick = (event: React.MouseEvent<HTMLElement>, vessel: Vessel) => {
    setActionMenuAnchor(event.currentTarget);
    setSelectedVessel(vessel);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchor(null);
    setSelectedVessel(null);
  };

  // Get unique values for filters
  const uniqueTypes = [...new Set(vessels.map(v => v.type))];
  const uniqueManufacturers = [...new Set(vessels.map(v => v.manufacturer))];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Powered Vessels Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/admin/equipment/vessels/new?propulsionType=powered')}
        >
          Add Powered Vessel
        </Button>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary">{stats.total}</Typography>
              <Typography variant="body2" color="text.secondary">Total Vessels</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="success.main">{stats.active}</Typography>
              <Typography variant="body2" color="text.secondary">Active</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="warning.main">{stats.maintenance}</Typography>
              <Typography variant="body2" color="text.secondary">In Maintenance</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="error.main">{stats.outOfService}</Typography>
              <Typography variant="body2" color="text.secondary">Out of Service</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Badge badgeContent={stats.needingAttention} color="error">
                <WarningIcon color="warning" fontSize="large" />
              </Badge>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Need Attention
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Search vessels..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="maintenance">Maintenance</MenuItem>
                <MenuItem value="out-of-service">Out of Service</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                <MenuItem value="all">All Types</MenuItem>
                {uniqueTypes.map(type => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Manufacturer</InputLabel>
              <Select value={manufacturerFilter} onChange={(e) => setManufacturerFilter(e.target.value)}>
                <MenuItem value="all">All Manufacturers</MenuItem>
                {uniqueManufacturers.map(manufacturer => (
                  <MenuItem key={manufacturer} value={manufacturer}>{manufacturer}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('all');
                  setTypeFilter('all');
                  setManufacturerFilter('all');
                }}
              >
                Clear Filters
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Vessels Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Manufacturer/Model</TableCell>
                <TableCell>Capacity</TableCell>
                <TableCell>Registration</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Alerts</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredVessels
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((vessel) => {
                  const alerts = getVesselAlerts(vessel);
                  return (
                    <TableRow key={vessel.id} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {vessel.name}
                          </Typography>
                          {vessel.year && (
                            <Typography variant="caption" color="text.secondary">
                              {vessel.year}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{vessel.type}</TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">{vessel.manufacturer}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {vessel.model}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{vessel.capacity} people</TableCell>
                      <TableCell>
                        <Typography variant="body2">{vessel.stateRegistrationNumber}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Expires: {vessel.stateRegistrationExpiry}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={vessel.status}
                          color={getStatusColor(vessel.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          {alerts.includes('maintenance') && (
                            <Tooltip title="Maintenance Due Soon">
                              <MaintenanceIcon color="warning" fontSize="small" />
                            </Tooltip>
                          )}
                          {alerts.includes('certification') && (
                            <Tooltip title="Certification Expiring Soon">
                              <CertificationIcon color="error" fontSize="small" />
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          onClick={(e) => handleActionMenuClick(e, vessel)}
                          size="small"
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredVessels.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleActionMenuClose}
      >
        <MenuItemAction onClick={() => {
          navigate(`/admin/equipment/vessels/view/${selectedVessel?.id}`);
          handleActionMenuClose();
        }}>
          <ListItemIcon><ViewIcon fontSize="small" /></ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItemAction>
        <MenuItemAction onClick={() => {
          navigate(`/admin/equipment/vessels/edit/${selectedVessel?.id}`);
          handleActionMenuClose();
        }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItemAction>
        <Divider />
        <MenuItemAction onClick={() => {
          if (selectedVessel) handleStatusChange(selectedVessel.id, 'active');
          handleActionMenuClose();
        }}>
          <ListItemIcon><CheckCircleIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Set Active</ListItemText>
        </MenuItemAction>
        <MenuItemAction onClick={() => {
          if (selectedVessel) handleStatusChange(selectedVessel.id, 'maintenance');
          handleActionMenuClose();
        }}>
          <ListItemIcon><MaintenanceIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Set Maintenance</ListItemText>
        </MenuItemAction>
        <MenuItemAction onClick={() => {
          if (selectedVessel) handleStatusChange(selectedVessel.id, 'out-of-service');
          handleActionMenuClose();
        }}>
          <ListItemIcon><WarningIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Set Out of Service</ListItemText>
        </MenuItemAction>
        <Divider />
        <MenuItemAction onClick={() => {
          setDeleteDialogOpen(true);
          handleActionMenuClose();
        }}>
          <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItemAction>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Vessel</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedVessel?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteVessel} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PoweredVesselsPage; 